/**
 * This file handles the "within the page" navigation,
 * as found on the Education - Care Providers page
 */

import { gsap, ScrollTrigger, ScrollToPlugin } from './cco-gsap-core';

const initializeSmoothScrolling = (pageNavigation, getScrollSpyOffset) => {
  const navLinks = pageNavigation.querySelectorAll('a[href^="#"]');
  
  console.log('Smooth scrolling initialized for', navLinks.length, 'nav links');
  
  navLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      
      const targetId = link.getAttribute('href');
      const targetElement = document.querySelector(targetId);
      
      console.log('Nav link clicked:', targetId, 'Target element found:', !!targetElement);
      
      if (targetElement) {
        const offset = getScrollSpyOffset();
        const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - offset + 15;
        
        console.log('Scrolling to position:', targetPosition, 'with offset:', offset);
        
        // Temporarily disable CSS smooth scrolling to prevent conflicts
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        const originalHtmlScrollBehavior = htmlElement.style.scrollBehavior;
        const originalBodyScrollBehavior = bodyElement.style.scrollBehavior;
        
        htmlElement.style.scrollBehavior = 'auto';
        bodyElement.style.scrollBehavior = 'auto';
        
        // Kill any existing scroll animations
        gsap.killTweensOf(window);
        
        gsap.to(window, {
          duration: 1,
          scrollTo: {
            y: targetPosition,
            autoKill: false
          },
          ease: "power2.out",
          overwrite: true,
          onComplete: () => {
            // Restore original scroll behavior
            htmlElement.style.scrollBehavior = originalHtmlScrollBehavior;
            bodyElement.style.scrollBehavior = originalBodyScrollBehavior;
            console.log('Scroll animation completed');
          }
        });
      } else {
        console.warn('Target element not found for:', targetId);
      }
    });
  });
};

const initializeScrollSpy = (pageNavigation, getScrollSpyOffset) => {
  // Scroll Spy Feature
  const navLinks = pageNavigation.querySelectorAll('a[href^="#"]');
  const sectionIds = Array.from(navLinks).map(link => link.getAttribute('href')).filter(id => id.startsWith('#'));
  const sections = sectionIds.map(id => document.querySelector(id)).filter(Boolean);
  
  if (sections.length === 0) {
    console.warn('No target sections found for scroll spy');
    return;
  }

  const highlightNav = () => {
    let activeIndex = -1;
    const offset = getScrollSpyOffset(); // Use dedicated scroll spy offset
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Find which section is currently in view
    sections.forEach((section, idx) => {
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top + scrollTop;
      const sectionBottom = sectionTop + rect.height;
      
      // Check if section is in viewport
      if (scrollTop >= sectionTop - offset && scrollTop < sectionBottom - offset) {
        activeIndex = idx;
      }
    });
    
    // If no section is active, check if we're at the top
    if (activeIndex === -1 && scrollTop < sections[0].getBoundingClientRect().top + scrollTop - offset) {
      activeIndex = 0;
    }
    
    // Update nav links
    navLinks.forEach((link, idx) => {
      if (idx === activeIndex) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });
    
    // Debug current state
    console.log('Active section index:', activeIndex, 'Scroll position:', scrollTop, 'Offset:', offset);
  };

  // Use throttled scroll for better performance
  let ticking = false;
  const throttledHighlight = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        highlightNav();
        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener('scroll', throttledHighlight);
  window.addEventListener('resize', highlightNav);
  highlightNav(); // Initial call
}

export const initializePageNavigation = () => {
  const pageNavigation = document.querySelector('.page-navigation');
  
  if (!pageNavigation) {
    console.log('Page navigation element not found');
    return;
  }
  
  // Function to get sticky position based on viewport
  const getStickyPosition = () => {
    // use a switch statement for multiple breakpoints
    switch (true) {
      case window.innerWidth >= 992: // lg
        return 112;
      default: // xs
        return 102;
    } 
  };

  // Function to get scroll spy offset based on viewport
  const getScrollSpyOffset = () => {
    switch (true) {
      case window.innerWidth >= 992: // lg (desktop)
        return 220;
      default: // xs (mobile)
        return 166;
    }
  };
  
  // Create ScrollTrigger for sticky behavior
  const createStickyTrigger = () => {
    return ScrollTrigger.create({
      trigger: pageNavigation,
      start: `top ${getStickyPosition()}px`,
      end: "max", // No end point - stays sticky indefinitely
      pin: true,
      pinSpacing: false
      // markers: true
    });
  };
  
  let stickyTrigger = createStickyTrigger();

  // Recreate ScrollTrigger on resize to adjust start position
  window.addEventListener('resize', () => {
    if (stickyTrigger) {
      stickyTrigger.kill();
    }
    stickyTrigger = createStickyTrigger();
  });

  // Initialize smooth scrolling with GSAP
  initializeSmoothScrolling(pageNavigation, getScrollSpyOffset);
  
  // Initialize scroll spy
  initializeScrollSpy(pageNavigation, getScrollSpyOffset);
}